'use client'

import { useEffect, useRef } from 'react'

import { useLatestCallback } from '@marketplace-web/shared/ui-state'
import {
  transformCatalogNavigationDtos,
  getCatalogInitializersParamsFromUrl,
  transformDynamicFilters,
  transformSelectedDynamicFilters,
  TransformedApiData,
  getCatalogInitializers,
  getCatalogItems,
  CATALOG_FETCH_DEBOUNCE_AMOUNT,
  CATALOG_PER_PAGE,
  parseCatalogItemsArgsFromUrl,
  getCatalogFacetedCategories,
} from '@marketplace-web/domain/catalog-data'
import {
  useCatalogContext,
  DataReducerAction,
  DtoReducerAction,
  DynamicFilterReducerAction,
  FilterReducerAction,
  GeneralReducerAction,
  ItemsReducerAction,
  FacetedCategoriesReducerAction,
} from '@marketplace-web/domain/catalog-page-data'
import { useAbTest } from '@marketplace-web/feature-flags/ab-tests-data'

import { isResponseSuccessful } from '@marketplace-web/core-api/api-client-util'
import { useLocation } from '@marketplace-web/shared/browser'
import { useDebounce } from '@marketplace-web/shared/ui-helpers'

import useFetchCatalogBrand from './useFetchCatalogBrand'
import useFetchCatalogConfigurations from './useFetchCatalogConfigurations'

import { incrementCatalogLoad } from '../utils/observability'
import useItemsCatalogQuery from './useItemsCatalogQuery'
import useItemsTracking from './useItemsTracking'

const useFetchCatalogData = () => {
  const { state, dispatch } = useCatalogContext()
  const fetchCatalogBrand = useFetchCatalogBrand()
  const fetchCatalogConfigurations = useFetchCatalogConfigurations()
  const { page, time, setQuery } = useItemsCatalogQuery()
  const { searchParams: currentSearchParams } = useLocation()
  const { onItemsSuccess } = useItemsTracking()

  const isCatalogServerSideFetchingEnabled =
    useAbTest('web_initial_server_side_loading')?.variant === 'on'

  const filtersRef = useRef(state.filters)
  filtersRef.current = state.filters

  const selectedDynamicFiltersRef = useRef(state.dynamicFilters.selectedDynamicFilters)
  selectedDynamicFiltersRef.current = state.dynamicFilters.selectedDynamicFilters

  const timeRef = useRef(time)
  timeRef.current = time

  const searchQuery = currentSearchParams.search_text ? String(currentSearchParams.search_text) : ''
  const searchQueryRef = useRef(searchQuery)
  searchQueryRef.current = searchQuery

  const searchByImageUuid = currentSearchParams.search_by_image_uuid
    ? String(currentSearchParams.search_by_image_uuid)
    : undefined

  const isInitialLoad = useRef(true)

  const fetchCatalogData = useLatestCallback(async ({ relativeUrl, searchParams }) => {
    if (isCatalogServerSideFetchingEnabled) return

    let catalogIds: Array<number> | undefined
    const catalogInitializersFromUrl = getCatalogInitializersParamsFromUrl(
      relativeUrl,
      searchParams,
    )

    dispatch({
      type: DataReducerAction.FETCH_DATA_REQUEST,
      payload: { relativeUrl, searchParams },
    })

    if (catalogInitializersFromUrl.catalog) {
      const { catalog } = catalogInitializersFromUrl

      if (Array.isArray(catalog)) {
        catalogIds = catalog.map(value => parseInt(value, 10))
      } else {
        catalogIds = [parseInt(catalog, 10)]
      }
    }

    // parse parameters from url
    const catalogItemsParams = parseCatalogItemsArgsFromUrl(searchParams, relativeUrl)

    if (catalogIds) {
      dispatch({
        type: DynamicFilterReducerAction.UPDATE_TEMPORARY_CATALOG_IDS,
        payload: { temporaryCatalogIds: catalogIds },
      })
    }

    // run API calls in parallel
    const [initializersResponse, itemsResponse, facetedCategoriesResponse] = await Promise.all([
      getCatalogInitializers(catalogInitializersFromUrl),
      getCatalogItems({
        ...catalogItemsParams,
        page,
        query: searchQuery,
        perPage: CATALOG_PER_PAGE,
        time,
        searchByImageUuid,
      }),
      getCatalogFacetedCategories({
        ...catalogItemsParams,
        time,
        filterSearchText: searchQuery,
      }),
      fetchCatalogConfigurations(),
    ])

    if (initializersResponse) {
      if ('errors' in initializersResponse) {
        dispatch({ type: DataReducerAction.FETCH_DATA_FAILURE, payload: undefined })
      } else {
        const { dtos, filters } = initializersResponse
        const currentBrand = state.catalogBrand.brand
        const data: TransformedApiData = {
          catalogTree: dtos.catalogs,
          catalogs: transformCatalogNavigationDtos(dtos.catalogs),
        }

        if (dtos.dynamicFilters && dtos.selectedDynamicFilters) {
          data.dynamicFilters = transformDynamicFilters(dtos.dynamicFilters)
          data.selectedDynamicFilters = transformSelectedDynamicFilters(dtos.selectedDynamicFilters)
        }

        dispatch({
          type: DtoReducerAction.CHANGE_DTO,
          payload: { dtos },
        })

        dispatch({
          type: DataReducerAction.FETCH_DATA_SUCCESS,
          payload: { catalogTree: data.catalogTree, catalogs: data.catalogs },
        })

        dispatch({
          type: DynamicFilterReducerAction.FETCH_DYNAMIC_FILTERS_SUCCESS,
          payload: {
            dynamicFilters: data.dynamicFilters || [],
            selectedDynamicFilters: data.selectedDynamicFilters || [],
          },
        })

        dispatch({
          type: FilterReducerAction.SET_INITIAL_FILTERS,
          payload: { filters },
        })

        if (!currentBrand) {
          fetchCatalogBrand({ selectedDynamicFilters: data.selectedDynamicFilters || [] })
        }
      }
    }

    if (itemsResponse) {
      if (isResponseSuccessful(itemsResponse)) {
        onItemsSuccess(itemsResponse)
        try {
          setQuery('time', itemsResponse.pagination.time)
          dispatch({
            type: GeneralReducerAction.SET_CATALOG_TITLE,
            payload: { title: itemsResponse.title },
          })
          incrementCatalogLoad('success', 'api_items')
        } catch {
          dispatch({
            type: ItemsReducerAction.FETCH_ITEMS_FAILURE,
            payload: { error: itemsResponse.message },
          })
          incrementCatalogLoad('error', 'api_items')
        }

        dispatch({
          type: ItemsReducerAction.FETCH_ITEMS_SUCCESS,
          payload: {
            items: itemsResponse.items,
            pagination: itemsResponse.pagination,
            replicaWarning: itemsResponse.replica_warning,
            searchTrackingParams: {
              correlationId: itemsResponse.search_tracking_params.search_correlation_id,
              sessionId: itemsResponse.search_tracking_params.search_session_id,
              globalSearchSessionId:
                itemsResponse.search_tracking_params.global_search_session_id || null,
              globalCatalogBrowseSessionId:
                itemsResponse.search_tracking_params.global_catalog_browse_session_id || null,
            },
          },
        })
      } else {
        dispatch({
          type: ItemsReducerAction.FETCH_ITEMS_FAILURE,
          payload: { error: itemsResponse.message },
        })
        incrementCatalogLoad('error', 'api_items')
      }
    }

    if (facetedCategoriesResponse) {
      if ('errors' in facetedCategoriesResponse) {
        dispatch({
          type: FacetedCategoriesReducerAction.FETCH_FACETED_CATEGORIES_FAILURE,
          payload: { error: facetedCategoriesResponse.message },
        })
      } else {
        dispatch({
          type: FacetedCategoriesReducerAction.FETCH_FACETED_CATEGORIES_SUCCESS,
          payload: { categories: facetedCategoriesResponse.categories },
        })
      }
    }

    // increment filtersChangeCount after initial load
    dispatch({
      type: GeneralReducerAction.INCREMENT_FILTERS_CHANGE_COUNT,
      payload: undefined,
    })
  })

  const fetchItemsAndFacetedCategories = useLatestCallback(async params => {
    const {
      page: itemPage,
      perPage,
      query,
      time: itemTime,
      searchByImageUuid: itemSearchByImageUuid,
      selectedDynamicFilters,
      ...filters
    } = params

    dispatch({ type: ItemsReducerAction.FETCH_ITEMS_REQUEST, payload: undefined })
    dispatch({
      type: FacetedCategoriesReducerAction.FETCH_FACETED_CATEGORIES_REQUEST,
      payload: undefined,
    })

    const [itemsResponse, facetedCategoriesResponse] = await Promise.all([
      getCatalogItems(params),
      getCatalogFacetedCategories({
        ...filters,
        selectedDynamicFilters,
        time: itemTime,
        filterSearchText: query,
      }),
    ])

    if (isResponseSuccessful(itemsResponse)) {
      onItemsSuccess(itemsResponse)

      try {
        setQuery('time', itemsResponse.pagination.time)
        dispatch({
          type: GeneralReducerAction.SET_CATALOG_TITLE,
          payload: { title: itemsResponse.title },
        })
        incrementCatalogLoad('success', 'api_items')
      } catch {
        dispatch({
          type: ItemsReducerAction.FETCH_ITEMS_FAILURE,
          payload: { error: itemsResponse.message },
        })
        incrementCatalogLoad('error', 'api_items')
      }

      dispatch({
        type: ItemsReducerAction.FETCH_ITEMS_SUCCESS,
        payload: {
          items: itemsResponse.items,
          pagination: itemsResponse.pagination,
          replicaWarning: itemsResponse.replica_warning,
          searchTrackingParams: {
            correlationId: itemsResponse.search_tracking_params.search_correlation_id,
            sessionId: itemsResponse.search_tracking_params.search_session_id,
            globalSearchSessionId:
              itemsResponse.search_tracking_params.global_search_session_id || null,
            globalCatalogBrowseSessionId:
              itemsResponse.search_tracking_params.global_catalog_browse_session_id || null,
          },
        },
      })
    } else {
      dispatch({
        type: ItemsReducerAction.FETCH_ITEMS_FAILURE,
        payload: { error: itemsResponse.message },
      })
      incrementCatalogLoad('error', 'api_items')
    }

    if (isResponseSuccessful(facetedCategoriesResponse)) {
      dispatch({
        type: FacetedCategoriesReducerAction.FETCH_FACETED_CATEGORIES_SUCCESS,
        payload: { categories: facetedCategoriesResponse.categories },
      })
    } else {
      dispatch({
        type: FacetedCategoriesReducerAction.FETCH_FACETED_CATEGORIES_FAILURE,
        payload: { error: facetedCategoriesResponse.message },
      })
    }
  })

  const fetchItemsAndFacetedCategoriesDebounced = useDebounce(
    fetchItemsAndFacetedCategories,
    CATALOG_FETCH_DEBOUNCE_AMOUNT,
  )

  // reset page to 1 when filters change
  useEffect(() => {
    if (state.general.filtersChangeCount <= 1) return
    setQuery('page', 1)
  }, [state.general.filtersChangeCount, setQuery])

  const globalSearchSessionId = useRef(state.searchSession.globalSearchSessionId)
  globalSearchSessionId.current = state.searchSession.globalSearchSessionId

  // fetch items when filters, page, or search params change
  useEffect(() => {
    if (state.general.filtersChangeCount === 0) return
    if (isInitialLoad.current) {
      isInitialLoad.current = false

      return
    }

    fetchItemsAndFacetedCategoriesDebounced({
      ...filtersRef.current,
      globalSearchSessionId: globalSearchSessionId.current,
      page,
      query: searchQueryRef.current,
      perPage: CATALOG_PER_PAGE,
      selectedDynamicFilters: selectedDynamicFiltersRef.current,
      time: timeRef.current,
      searchByImageUuid,
    })
  }, [
    state.general.filtersChangeCount,
    page,
    searchByImageUuid,
    fetchItemsAndFacetedCategoriesDebounced,
  ])

  return fetchCatalogData
}

export default useFetchCatalogData
